/**
 * Fetches a remote file and triggers a browser download (Save / Save as).
 * Use when the URL may be cross-origin but allows CORS for GET.
 */

export type DownloadFileResult = "local" | "fallback";

export const downloadFileFromUrl = async (
  url: string,
  filename: string
): Promise<void> => {
  const res = await fetch(url, { method: "GET", mode: "cors" });
  if (!res.ok) {
    throw new Error(`Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename.trim() || "document.pdf";
  link.style.display = "none";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 2000);
};

/** Tries a local download; on failure opens the URL in a new tab (e.g. CORS). */
export const downloadFileFromUrlWithFallback = async (
  url: string,
  filename: string
): Promise<DownloadFileResult> => {
  try {
    await downloadFileFromUrl(url, filename);
    return "local";
  } catch {
    try {
      window.open(url, "_blank", "noopener,noreferrer");
      return "fallback";
    } catch {
      throw new Error("Failed to download or open the file");
    }
  }
};
